import { Button, CloseButton } from '@ui/shared/Button';
import isObject from 'lodash/isObject';
import sortBy from 'lodash/sortBy';
import * as React from 'react';
import { combineLatest } from 'rxjs';
import styled from 'styled-components';
import {
  activeMetrics,
  IMetrics,
} from '../../../data/providers/metricsProvider';
import { activity } from '../../../data/providers/testsProvider';
import { IMetric, ITest } from '../../../data/types';
import { useRepetitions } from '../../../hooks/repetitions/useRepetitions';
import { FullHeightColumn } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { CardBody } from '../../shared/card/CardBody';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ForceTime } from '../../shared/graphs/ForceTime';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { Scoring } from '../test/scoring/Scoring';
import { ActivityList } from './ActivityList';

const excludedKeys = ['athlete', 'groups', 'tags', 'teams', 'testType'];

interface IProps {
  onClose: () => void;
}
const Activity = (props: IProps): React.ReactElement => {
  const [feedData, setFeedData] = React.useState([]);
  const [feedLoaded, setFeedLoaded] = React.useState(false);
  const [feedPaused, setFeedPaused] = React.useState(false);
  const [selectedTest, setSelectedTest] = React.useState(null);
  const [activeMetricMap, setActiveMetricsMap] = React.useState<IMetrics>();

  React.useEffect((): (() => void) => {
    const subscriber = combineLatest([activity(), activeMetrics]).subscribe(
      (results: [ITest[], IMetrics]): void => {
        const [tests, mActiveMetrics] = results;
        setFeedData(tests);
        setActiveMetricsMap(mActiveMetrics);
        setFeedLoaded(true);
      }
    );
    return (): void => {
      subscriber.unsubscribe();
    };
  }, []);

  /**
   * @description subscribe to active metrics settings
   */
  React.useEffect((): (() => void) => {
    const subscriber = activeMetrics.subscribe(
      (mActiveMetrics: IMetrics): void => {
        setActiveMetricsMap(mActiveMetrics);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  /**
   * @description handles the user selecting a specific test for the list
   * @param id {string}
   */
  const selectTest = (id: string): void => {
    setSelectedTest(id);
    setFeedPaused(true);
  };

  const currentTest = React.useMemo((): ITest | null => {
    if (feedData.length <= 0) {
      return null;
    }
    return feedPaused && selectedTest
      ? feedData.find((item: ITest): boolean => item.id === selectedTest)
      : feedData[0];
  }, [feedPaused, selectedTest, feedData]);

  const { selectedRepetitionChangeStream, repetitions, selectedRepetition } =
    useRepetitions(currentTest?.id);

  const metrics = React.useMemo((): IMetric[] => {
    if (!currentTest) return [];

    if (selectedRepetition !== null) {
      return sortBy(
        selectedRepetition.metrics,
        (metric: IMetric): number => metric.order
      ).filter((value: IMetric): boolean =>
        Boolean(activeMetricMap[value?.id])
      );
    } else {
      return sortBy(
        Object.keys(currentTest)
          .filter(
            (key: string): boolean =>
              !excludedKeys.includes(key) &&
              Boolean(activeMetricMap[currentTest?.testType?.id]?.[key])
          )
          .map((key: string): IMetric => currentTest[key])
          .filter((value: IMetric): boolean => isObject(value)),
        (metric: IMetric): number => metric.order
      );
    }
  }, [
    JSON.stringify(currentTest),
    JSON.stringify(activeMetricMap),
    JSON.stringify(selectedRepetition),
  ]);

  const toggleFeed = (): void => {
    setFeedPaused(!feedPaused);
  };

  return (
    <PopupCard onClose={props.onClose} size='full' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6 style={{ marginLeft: '15px' }}>Live Testing</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody
          style={{
            backgroundColor: 'var(--color-surface-0)',
          }}
        >
          <Row shouldCollapse>
            <TestColumnLeft>
              <ActivityList
                selectTest={selectTest}
                selectedTest={selectedTest}
                data={feedData}
                toggleFeed={toggleFeed}
                feedPaused={feedPaused}
                loading={!feedLoaded}
              />
            </TestColumnLeft>
            <TestColumnRight>
              <ForceTime
                test={currentTest}
                selectedRepetitionChangeStream={selectedRepetitionChangeStream}
                repetitions={repetitions}
                selectedRepetition={selectedRepetition}
              />
              <Scoring test={currentTest} metrics={metrics} level={1} />
            </TestColumnRight>
          </Row>
        </CardBody>
      </PopupCardContent>
    </PopupCard>
  );
};

export { Activity };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TestColumnLeft = styled(FullHeightColumn as any)`
  flex: 20;
  padding-bottom: var(--grid-gap);
  padding-left: var(--grid-gap);
  padding-right: var(--grid-gap);
  @media (max-width: 990px) {
    order: 2;
    padding-left: 5px;
    padding-right: 0px;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TestColumnRight = styled(FullHeightColumn as any)`
  flex: 80;
  padding-right: var(--grid-gap);
  padding-bottom: var(--grid-gap);
  @media (max-width: 990px) {
    order: 1;
    padding-left: 5px;
    padding-right: 0px;
    padding-bottom: 0px;
  }
`;
